import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from './ui/button';
import { CheckCircle, Package, BookOpen, ArrowRight } from 'lucide-react';

const HardcoverSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing');
  const [orderData, setOrderData] = useState(null);

  const orderId = searchParams.get('order_id');
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (orderId) processOrder();
  }, [orderId]);

  const processOrder = async () => {
    try {
      const BACKEND = process.env.REACT_APP_BACKEND_URL;
      // Trigger backend to generate PDFs and submit to Lulu
      const res = await fetch(`${BACKEND}/api/hardcover/process/${orderId}`, { method: 'POST' });
      const data = await res.json();
      setOrderData(data);
      setStatus(data.lulu_job_id ? 'success' : 'pending');
    } catch (err) {
      console.error('Process error:', err);
      setStatus('pending');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-50 flex items-center justify-center px-4 py-12" data-testid="hardcover-success-page">
      <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12 max-w-lg w-full text-center fade-in">
        {status === 'processing' ? (
          <>
            <div className="w-16 h-16 border-4 border-purple-200 border-t-purple-500 rounded-full animate-spin mx-auto mb-6" />
            <h2 className="text-2xl font-bold text-gray-800 mb-3">Processing Your Order...</h2>
            <p className="text-gray-600">We're generating your print-ready book and sending it to the printer. This may take a moment.</p>
          </>
        ) : (
          <>
            <div className="w-20 h-20 bg-gradient-to-br from-green-100 to-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-500" />
            </div>

            <h2 className="text-2xl font-bold text-gray-800 mb-3" data-testid="success-title">
              {status === 'success' ? 'Your Hardcover Book is Being Printed!' : 'Order Confirmed!'}
            </h2>

            <p className="text-gray-600 mb-6">
              {status === 'success'
                ? 'Your autobiography has been sent to our professional printing partner. It will be printed and shipped directly to you.'
                : 'Your order has been confirmed. Our team will process your book and ship it soon.'}
            </p>

            <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-5 mb-6 text-left space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <Package className="w-4 h-4 text-purple-500" />
                <span className="text-gray-600">Order ID:</span>
                <span className="font-mono font-medium text-gray-800 text-xs">{orderId?.substring(0, 8)}...</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <BookOpen className="w-4 h-4 text-purple-500" />
                <span className="text-gray-600">Status:</span>
                <span className="font-medium text-green-600">{status === 'success' ? 'Sent to Printer' : 'Confirmed'}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Package className="w-4 h-4 text-purple-500" />
                <span className="text-gray-600">Estimated Delivery:</span>
                <span className="font-medium text-gray-800">7-10 business days</span>
              </div>
            </div>

            <div className="space-y-3">
              <Button onClick={() => navigate('/dashboard')} className="w-full btn-primary text-white py-5 rounded-full border-0 font-semibold" data-testid="go-to-dashboard">
                Go to Dashboard <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button variant="outline" onClick={() => navigate('/')} className="w-full py-5 rounded-full" data-testid="back-to-home">
                Back to Home
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default HardcoverSuccess;
